import { getAgeGroup } from "db/WAIS-ATT-MDT/wais-scoring";
import { makeParquetQuerier } from "./parquetQuery";

const queryParquet = makeParquetQuerier("WAIS-EMPANS");

// kind → WAIS-IV parquet file
const KIND_FILE = { emcd: "emcd.parquet", emci: "emci.parquet", emcc: "emcc.parquet" };
// kind → WAIS-III column name
const KIND_W3_COL = { emcd: "direct", emci: "inverse" };

/**
 * Look up cumulative % for a given empan value from rows.
 * Returns the cum% or null if not found / NaN.
 */
function lookupCumPct(rows, empan, col) {
  const row = rows.find((r) => r.empan === empan);
  if (!row) return null;
  const val = row[col];
  if (val == null || Number.isNaN(val)) return null;
  return val;
}

/**
 * Calculate empan percentile range.
 *
 * PC = [ round(100 - cumPct(empan)),  round(100 - cumPct(empan + 1)) ]
 *
 * cumPct(empan) = % of normative sample with empan >= value
 * so 100 - cumPct = % scoring below = lower bound of percentile
 * and 100 - cumPct(empan+1) = upper bound
 *
 * @param {number} age - Patient age in years
 * @param {number} empan - Raw empan score (0–9)
 * @param {string} kind - "emcd" | "emci" | "emcc"
 * @returns {Promise<{pc: [number, number], normSource: string} | {error: string}>}
 */
export async function calcEmpanPC(age, empan, kind) {
  const ageGroup = getAgeGroup(age);
  if (!ageGroup) return { error: `Âge ${age} hors plage (16-89)` };

  const isWais3 = ageGroup === "80-89";

  if (isWais3) {
    const col = KIND_W3_COL[kind];
    if (!col) return { error: `Pas de norme WAIS-III pour ${kind.toUpperCase()} (80-89 ans)` };

    const rows = await queryParquet("empan_wais3.parquet");
    const cumThis = lookupCumPct(rows, empan, col);
    const cumNext = lookupCumPct(rows, empan + 1, col);

    const pcLow = cumThis != null ? Math.round(100 - cumThis) : null;
    const pcHigh = cumNext != null ? Math.round(100 - cumNext) : 100;

    return { pc: [pcLow, pcHigh], normSource: "WAIS-III" };
  }

  // WAIS-IV (ages 16-79)
  const file = KIND_FILE[kind];
  if (!file) return { error: `Type inconnu : ${kind}` };

  const rows = await queryParquet(file);
  const col = ageGroup;
  const cumThis = lookupCumPct(rows, empan, col);
  const cumNext = lookupCumPct(rows, empan + 1, col);

  const pcLow = cumThis != null ? Math.round(100 - cumThis) : null;
  const pcHigh = cumNext != null ? Math.round(100 - cumNext) : 100;

  return { pc: [pcLow, pcHigh], normSource: "WAIS-IV" };
}
